import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser, UserRole } from "../../../../../services/UserContext";
import { useAlert } from "../../../../../services/AlertContext";
import useFetch from "../../../../../services/functions/useFetch";
import snowflake from "../../../../../assets/icons/loading-snowflake.svg";
import CustomAlert from "../../../../CustomAlert";
import FileUpload, { MediaType } from "./FileUpload";

interface AgeResponse {
  ages: SelectOption[];
}

interface SkillResponse {
  skills: SelectOption[];
}

interface SelectOption {
  id: number;
  title: string;
}

interface IdResponse {
  id: number;
}

interface ExerciseResponse {
  id: number;
  age_id: number;
  skill_id: number;
  title: string;
  description: string;
  thumbnail: number;
  sort: number;
  pic_id: number[];
  vid_id: number[];
}

// Use environment variable from .env-file
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function EditContent() {
  //   const [count, setCount] = useState<number>(0);
  const query = useQuery();
  const { role } = useUser();
  const { triggerAlert } = useAlert();
  const { error, isPending, fetchData } = useFetch();

  const navigate = useNavigate();

  const [id, setId] = useState<string>("");
  const [ageOptions, setAgeOptions] = useState<SelectOption[]>([]);
  const [skillOptions, setSkillOptions] = useState<SelectOption[]>([]);

  const [imageList, setImageList] = useState<{ source: File | string }[]>([]);
  const [videoList, setVideoList] = useState<{ source: File | string }[]>([]);

  const [initialImageList, setInitialImageList] = useState<number[]>([]);
  const [deletedImages, setDeletedImages] = useState<number[]>([]);

  const [initialVideoList, setInitialVideoList] = useState<number[]>([]);
  const [deletedVideos, setDeletedVideos] = useState<number[]>([]);

  const [errorMsg, setErrorMsg] = useState<string>("");

  const [exercise, setExercise] = useState<ExerciseResponse>({
    id: 0,
    age_id: 0,
    skill_id: 0,
    title: "",
    description: "",
    thumbnail: 0,
    sort: 0,
    pic_id: [],
    vid_id: [],
  });

  const populateExercise = (exercises: ExerciseResponse) => {
    //console.log(exercises);
    if (!exercises) return;

    const localImageList: { source: File | string }[] = [];
    const localVideoList: { source: File | string }[] = [];
    exercises.pic_id.forEach((image) => {
      //console.log("id: " + image);
      if (image) localImageList.push({ source: `${apiBaseUrl}/pic/${image}` });
    });
    exercises.vid_id.forEach((video) => {
      //console.log("id: " + video);
      if (video) localVideoList.push({ source: `${apiBaseUrl}/vid/${video}` });
    });
    setImageList(localImageList);
    setVideoList(localVideoList);

    setExercise(exercises);
  };

  const extractImageId = (url: File | string): string | null => {
    if (typeof url !== "string") return null; // Ensure url is a string before calling .match()
    const match = url.match(/\/pic\/(\d+)/);
    return match ? match[1] : null;
  };

  const extractVideoId = (url: File | string): string | null => {
    if (typeof url !== "string") return null; // Ensure url is a string before calling .match()
    const match = url.match(/\/vid\/(\d+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    // die ID des Eintrags aus der URL extrahieren
    setId(query.get("id") ?? "");

    setErrorMsg("");

    if (UserRole.USER) {
      setErrorMsg("Keine Zugriffsberechtigung!");
    }
  }, [query]);

  useEffect(() => {
    if (id) {
      fetchData(`/get/exercise/${id}`, "GET", {
        doOnSuccess: (response) => {
          const typedResponse = response as ExerciseResponse;
          setInitialImageList(typedResponse.pic_id);
          setInitialVideoList(typedResponse.vid_id);
          populateExercise(typedResponse);
        },
        doOnError: () => {
          console.error("Failed to fetch exercise.");
        },
      });
    }
  }, [id]);

  useEffect(() => {
    const extractedImageIds = imageList
      .map((image) => extractImageId(image.source as string))
      .filter((id): id is string => id !== null)
      .map(Number);

    const extractedVideoIds = videoList
      .map((video) => extractVideoId(video.source as string))
      .filter((id): id is string => id !== null)
      .map(Number);

    // Find deleted images
    const deletedImages = initialImageList.filter(
      (id) => !extractedImageIds.includes(id)
    );

    // Find deleted videos
    const deletedVideos = initialVideoList.filter(
      (id) => !extractedVideoIds.includes(id)
    );

    setDeletedImages(deletedImages);
    setDeletedVideos(deletedVideos);

    //console.log("Deleted Images:", deletedImages);
    //console.log("Deleted Videos:", deletedVideos);
  }, [imageList, initialImageList, videoList, initialVideoList]);

  useEffect(() => {
    fetchData(`/get/ages/`, "GET", {
      doOnSuccess: (response) => {
        const typedResponse = response as AgeResponse;
        setAgeOptions(typedResponse.ages);
      },
      doOnError: () => {
        console.error("Failed to fetch.");
      },
    });

    fetchData(`/get/skills/`, "GET", {
      doOnSuccess: (response) => {
        const typedResponse = response as SkillResponse;
        setSkillOptions(typedResponse.skills);
      },
      doOnError: () => {
        console.error("Failed to fetch.");
      },
    });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !exercise?.id ||
      !exercise?.age_id ||
      !exercise?.skill_id ||
      !exercise?.title ||
      !exercise?.description ||
      !exercise?.thumbnail
    ) {
      console.error("Es müssen alle Felder ausgefüllt werden");
      setErrorMsg("Es müssen alle Felder ausgefüllt werden");
      return;
    }

    // Prepare the data payload
    const payload = {
      id: Number(exercise?.id),
      age_id: Number(exercise?.age_id),
      skill_id: Number(exercise?.skill_id),
      title: exercise?.title,
      description: exercise?.description,
      thumbnail: Number(exercise?.thumbnail),
      sort: Number(exercise?.sort),
    };

    fetchData(`/update/exercise`, "POST", {
      body: payload,
      doOnSuccess: async (response) => {
        const updateResponse = response as IdResponse;
        setErrorMsg("");

        await uploadFiles(imageList, updateResponse.id, "pic");
        await uploadFiles(videoList, updateResponse.id, "vid");

        deletePictures();
        deleteVideos();

        triggerAlert("Übung erfolgreich geändert", "success", 4000);
      },
      doOnError: () => {
        setErrorMsg("Erstellung fehlgeschlagen. Bitte erneut versuchen.");
        console.error("Failed to create exercise.");
      },
    });
  };

  const uploadFiles = async (
    fileList: { source: File | string }[],
    exerciseId: number,
    type: "pic" | "vid"
  ) => {
    try {
      for (const [index, file] of fileList.entries()) {
        const formData = new FormData();
        formData.append("file", file.source); // Append one file at a time

        // Only try to upload new files, not the existing embedded pictures which are strings
        if (file.source instanceof File) {
          const response = await fetchData<IdResponse>(
            `/upload/${type}/?exercise_id=${exerciseId}`,
            "POST",
            {
              body: formData,
            }
          );

          if (index === 0 && type === "pic") {
            await setThumbnail(exerciseId, response.id);
          }
        }
      }

      // Clear error message once all uploads are successful
      setErrorMsg("");
      //console.log("All pictures uploaded successfully.");
    } catch (error) {
      console.error(error);
      setErrorMsg(`Upload failed: ${error}`);
    }
  };

  const setThumbnail = async (exercise_id: Number, thumbnail: Number) => {
    const payload = {
      id: exercise_id,
      age_id: Number(exercise?.age_id),
      skill_id: Number(exercise?.skill_id),
      thumbnail: thumbnail,
      title: exercise?.title,
      description: exercise?.description,
      sort: Number(exercise?.sort),
    };

    try {
      fetchData(`/update/exercise`, "POST", {
        body: payload,
        doOnSuccess: () => {
          setErrorMsg("");
          //console.log("Thumbnail set successfully.");
          navigate(-1);
        },
        doOnError: () => {
          setErrorMsg("Setting thumbnail failed. Please try again.");
          console.error("Failed to set thumbnail.");
        },
      });
    } catch (error) {
      console.error("Failed to update thumbnail:", error);
      setErrorMsg("Failed to update thumbnail. Please try again.");
    }
  };

  const deletePictures = () => {
    if (deletedImages.length === 0) return;

    deletedImages.forEach((image) => {
      const payload = { id: Number(image) };

      fetchData(`/delete/pic`, "DELETE", {
        body: payload,
        doOnSuccess: () => {
          //console.log(`Image ${image} deleted successfully.`);
        },
        doOnError: () => {
          setErrorMsg(
            "Löschen des Bildes fehlgeschlagen. Bitte erneut versuchen."
          );
          console.error(`Failed to delete image ${image}.`);
        },
      });
    });

    setDeletedImages([]);
  };

  const deleteVideos = () => {
    if (deletedVideos.length === 0) return;

    deletedVideos.forEach((video) => {
      const payload = { id: Number(video) };

      fetchData(`/delete/vid`, "DELETE", {
        body: payload,
        doOnSuccess: () => {
          //console.log(`Video ${video} deleted successfully.`);
        },
        doOnError: () => {
          setErrorMsg(
            "Löschen des Videos fehlgeschlagen. Bitte erneut versuchen."
          );
          console.error(`Failed to delete video ${video}.`);
        },
      });
    });

    setDeletedVideos([]);
  };

  return (
    <>
      {error && <CustomAlert message={error} type="error" />}
      {isPending ? (
        <img src={snowflake} alt="loading" className="loading-snowflake" />
      ) : (
        <section className="section-container">
          <div className="content-wrapper">
            <div className="form-container">
              <h1 className="form-title">Übung bearbeiten</h1>
              <form className="form" onSubmit={handleSubmit} noValidate>
                {errorMsg && <p className="form-error">{errorMsg}</p>}

                {role === UserRole.ADMIN || role === UserRole.EDITOR ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="age" className="form-label">
                        Altersklasse
                      </label>
                      <select
                        id="age"
                        value={exercise?.age_id}
                        onChange={(e) => {
                          const newAgeId = parseInt(e.target.value, 10); // Select Values sind immer Strings, deshalb Pase in Integer zur Basis 10 (Dezimalsystem)
                          setExercise((prevExercise) => ({
                            ...prevExercise,
                            age_id: newAgeId,
                          }));
                        }}
                        className="form-input"
                        required
                      >
                        <option
                          key="0"
                          value=""
                          disabled
                          style={{ color: "#dedbd5" }}
                        >
                          Altersklasse wählen:
                        </option>
                        {ageOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="skill" className="form-label">
                        Skillklasse
                      </label>
                      <select
                        id="skill"
                        value={exercise?.skill_id}
                        onChange={(e) => {
                          const newSkillId = parseInt(e.target.value, 10); // Select Values sind immer Strings, deshalb Pase in Integer zur Basis 10 (Dezimalsystem)
                          setExercise((prevExercise) => ({
                            ...prevExercise,
                            skill_id: newSkillId,
                          }));
                        }}
                        className="form-input"
                        required
                      >
                        <option
                          key="0"
                          value=""
                          disabled
                          style={{ color: "#dedbd5" }}
                        >
                          Skillklasse wählen:
                        </option>
                        {skillOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="password" className="form-label">
                        Titel
                      </label>
                      <input
                        type="text"
                        id="title"
                        autoComplete="off"
                        value={exercise?.title}
                        onChange={(e) => {
                          setExercise((prevExercise) => ({
                            ...prevExercise,
                            title: e.target.value,
                          }));
                        }}
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="description" className="form-label">
                        Beschreibung
                      </label>
                      <textarea
                        id="description"
                        autoComplete="off"
                        value={exercise?.description}
                        onChange={(e) => {
                          setExercise((prevExercise) => ({
                            ...prevExercise,
                            description: e.target.value,
                          }));
                        }}
                        className="form-area"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sort" className="form-label">
                        Sortierrangfolge (klein=oben | groß=unten; 3-stellige
                        Eingabe möglich)
                      </label>
                      <input
                        type="number"
                        id="sort"
                        autoComplete="off"
                        value={exercise?.sort}
                        onChange={(e) => {
                          const newSort = Number(e.target.value);
                          setExercise((prevExercise) => ({
                            ...prevExercise,
                            sort: newSort,
                          }));
                        }}
                        className="form-input"
                        min="0"
                        max="999"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="image" className="form-label">
                        Bilder auswählen
                      </label>
                      <FileUpload
                        accept=".png,.jpg"
                        idName="imageList"
                        mediaType={MediaType.Image}
                        infoText="Bilder können per Drag & Drop in der Reihenfolge verändert werden!<br />
                                                        → Das erste Bild wird automatisch als Hauptbild verwendet."
                        onFilesSelected={setImageList}
                        files={imageList}
                        setFiles={setImageList}
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ paddingBottom: ".5rem" }}
                    >
                      <label htmlFor="video" className="form-label">
                        Videos auswählen
                      </label>
                      <FileUpload
                        accept=".mp4,.mkv,.m4p,.mov"
                        idName="videoList"
                        mediaType={MediaType.Video}
                        infoText="Videos können per Drag & Drop in der Reihenfolge verändert werden!"
                        onFilesSelected={setVideoList}
                        files={videoList}
                        setFiles={setVideoList}
                      />
                    </div>

                    <button type="submit" className="form-button">
                      Änderung übernehmen
                    </button>
                  </>
                ) : (
                  <p className="form-error">Keine Berechtigung</p>
                )}
              </form>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default EditContent;
